import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Card,
  ErrorPanel,
  Loading,
  PageHeader,
} from "../components/ResourceView";
import {
  operationsJobsApi,
  type DispatchFieldFeed,
  type OperationsDivision,
  type OperationsJobListItem,
  type OperationsJobsWorkspace,
} from "../lib/operationsJobs";
import { useRpcResource } from "../lib/useRpcResource";
import { useOrganization } from "../org/OrganizationProvider";
import { NationalDispatchResourceBoardPage } from "./NationalDispatchResourceBoardPage";

function label(value: string | null | undefined) {
  return value ? value.replaceAll("_", " ") : "—";
}
function dateTime(value: string | null | undefined) {
  return value ? new Date(value).toLocaleString() : "—";
}
function nextWindow(item: OperationsJobListItem) {
  return (
    item.delivery_window_start ??
    item.install_window_start ??
    item.pickup_window_start
  );
}
function jobHref(division: OperationsDivision, id: string) {
  return division === "national"
    ? `/national/jobs/${id}`
    : `/operations/jobs/${id}`;
}

export function OperationsJobsPage({
  division = "local_event",
}: {
  division?: OperationsDivision;
}) {
  const { organizationId } = useOrganization();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState({ status: "", search: "" });
  const national = division === "national";
  const r = useRpcResource(
    () =>
      organizationId
        ? operationsJobsApi.list(
            organizationId,
            query.status || null,
            query.search || null,
          )
        : Promise.resolve({
            data: null,
            error: { message: "Organization context is missing." },
          }),
    [organizationId, query.status, query.search],
  );
  const feed = useRpcResource(
    () =>
      organizationId
        ? operationsJobsApi.fieldFeed(organizationId, 40)
        : Promise.resolve({ data: null, error: null }),
    [organizationId],
  );
  const data = r.data;
  const items = (data?.items ?? []).filter(
    (x) => x.operations_division === division,
  );
  const feedItems = (feed.data?.items ?? []).filter(
    (x) => x.operations_division === division,
  );
  const title = national
    ? "National Operations & Dispatch"
    : "Local Jobs & Dispatch";
  const subtitle = national
    ? "Paid Orders marked National by Sales route here. Open the Work Order to schedule every phase, install crews and third-party freight from one screen."
    : "Paid Local Event Orders route here for company-fleet, local crew and event execution.";
  if (national && params.get("view") === "resources")
    return <NationalDispatchResourceBoardPage />;
  return (
    <>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <>
            {national && (
              <Link className="button-link" to="/national/jobs?view=resources">
                Crew & Personnel Board
              </Link>
            )}
            <Link
              className="button-link"
              to={national ? "/national/field" : "/field/crew"}
            >
              {national
                ? "National Field Operations"
                : "Local Field Operations"}
            </Link>
            <button
              onClick={() => {
                void r.reload();
                void feed.reload();
              }}
            >
              Refresh
            </button>
          </>
        }
      />
      <Card>
        <form
          className="filter-bar operations-filter"
          onSubmit={(e) => {
            e.preventDefault();
            setQuery({ status, search });
          }}
        >
          <label className="field">
            <span>Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All active statuses</option>
              {(data?.status_options ?? []).map((x) => (
                <option key={x} value={x}>
                  {label(x)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Search</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Job name, reference, customer, site or city"
            />
          </label>
          <div className="row-actions">
            <button className="primary" type="submit">
              Apply filters
            </button>
            <button
              type="button"
              onClick={() => {
                setStatus("");
                setSearch("");
                setQuery({ status: "", search: "" });
              }}
            >
              Clear
            </button>
          </div>
        </form>
      </Card>
      {r.loading ? (
        <Loading />
      ) : r.error ? (
        <ErrorPanel message={r.error} />
      ) : !data ? (
        <ErrorPanel message="Job workspace did not return data." />
      ) : (
        <>
          <Workspace items={items} division={division} />
          <FieldFeed items={feedItems} division={division} />
        </>
      )}
    </>
  );
}

function Workspace({
  items,
  division,
}: {
  items: OperationsJobListItem[];
  division: OperationsDivision;
}) {
  const active = items.filter(
    (x) => !["completed", "cancelled", "closed"].includes(x.status),
  ).length;
  const ready = items.filter(
    (x) => x.status === "dispatch_ready" || x.dispatch_status === "ready",
  ).length;
  const scheduled = items.filter((x) => x.status === "scheduled").length;
  const inProgress = items.filter((x) => x.status === "in_progress").length;
  const blocking = items.filter((x) => x.blocking_gate_count > 0).length;
  return (
    <>
      <div className="metric-grid five">
        <Metric label="Active Jobs" value={active} />
        <Metric label="Dispatch ready" value={ready} />
        <Metric label="Scheduled" value={scheduled} />
        <Metric label="In progress" value={inProgress} />
        <Metric label="Blocking gates" value={blocking} />
      </div>
      <section className="table-card">
        {items.length === 0 ? (
          <div className="empty-state">
            <strong>No Jobs match this workspace.</strong>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Customer / Site</th>
                  <th>Status</th>
                  <th>Phases / Staffing</th>
                  <th>Gates</th>
                  <th>Next window</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <Link
                        className="record-link"
                        to={jobHref(division, item.id)}
                      >
                        <strong>{item.job_name}</strong>
                        <span>
                          Ref {item.job_number} ·{" "}
                          {item.fulfillment_location ??
                            "No fulfillment location"}
                        </span>
                      </Link>
                    </td>
                    <td>
                      <strong>
                        {item.account_name ?? "Unassigned customer"}
                      </strong>
                      <small>
                        {[
                          item.site_name,
                          item.site_city,
                          item.site_state_region,
                        ]
                          .filter(Boolean)
                          .join(" · ") || "No site details"}
                      </small>
                    </td>
                    <td>
                      <span
                        className={`status-pill status-${label(item.status).replaceAll(" ", "-")}`}
                      >
                        {label(item.status)}
                      </span>
                      <small>Dispatch: {label(item.dispatch_status)}</small>
                    </td>
                    <td>
                      {item.phase_count} phases
                      <small>
                        {item.unscheduled_phase_count} unscheduled ·{" "}
                        {item.active_employee_assignments} employees ·{" "}
                        {item.active_crew_assignments} crews
                      </small>
                    </td>
                    <td>
                      {item.blocking_gate_count === 0 ? (
                        <span className="status-pill status-current">
                          Clear
                        </span>
                      ) : (
                        <span className="status-pill status-blocking">
                          {item.blocking_gate_count} blocking
                        </span>
                      )}
                    </td>
                    <td>{dateTime(nextWindow(item))}</td>
                    <td>
                      <Link
                        className="button-link"
                        to={jobHref(division, item.id)}
                      >
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function FieldFeed({
  items,
  division,
}: {
  items: DispatchFieldFeed["items"];
  division: OperationsDivision;
}) {
  return (
    <Card title="Live field updates">
      {items.length === 0 ? (
        <div className="empty-state">No supervisor updates yet.</div>
      ) : (
        <div className="dispatch-field-feed">
          {items.map((x) => (
            <Link
              to={jobHref(division, x.job_id)}
              className="dispatch-field-update"
              key={x.event_id}
            >
              <div>
                <strong>
                  {x.job_name} · {x.crew_name ?? label(x.phase_type)}
                </strong>
                <span>
                  {x.event_type === "field.supervisor_update"
                    ? `${label(x.update_type)}${x.progress_percent == null ? "" : ` · ${x.progress_percent}%`}`
                    : `${x.employee_name ?? "Crew member"} · ${x.event_type.endsWith("clocked_in") ? "clocked in" : "clocked out"}`}
                </span>
                {x.message && <small>{x.message}</small>}
              </div>
              <time>{dateTime(x.event_at)}</time>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}
function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>
        {typeof value === "number"
          ? new Intl.NumberFormat("en-US").format(value)
          : value}
      </strong>
    </div>
  );
}
